import React, { useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete } from "components/Autocomplete";
import { styled } from "@mui/material/styles";
import { useGetCaseLabels } from "hooks/case-labels/useGetCaseLabels";
import { usePostCaseLabel } from "hooks/case-labels/usePostCaseLabel";
import { Case } from "interfaces/case";
import { CaseLabel } from "interfaces/case-label";
import { AutocompleteRenderGetTagProps, Chip, Tooltip } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";

interface AddCaseLabelProps {
  labels: { id: number; name: string }[];
  onChange: (params: { isValid: boolean; model: Case }) => void;
  caseModel: Case;
}

const MAX_OPTION_LENGTH = 25;
const MAX_LABEL_LENGTH = 100;

const replaceWhiteSpaces = (label: string): string => {
  return label.replace(/\s\s+/g, " ");
};

export const AddCaseLabel = ({
  labels,
  onChange,
  caseModel
}: AddCaseLabelProps): JSX.Element => {
  const [newLabel, setNewLabel] = useState("");
  const [selectedLabels, setSelectedLabels] = useState<CaseLabel[]>([]);
  const [newLabels, setNewLabels] = useState<CaseLabel[]>([]);
  const [labelsUpdated, setLabelsUpdated] = useState(false);
  const prevLabelsRef = useRef<CaseLabel[]>(
    caseModel.labels.map(label => ({ ...label, create_user: -1 }))
  );

  const validationSchema = Yup.object({
    label: Yup.string().max(
      MAX_LABEL_LENGTH,
      `Label cannot be longer than ${MAX_LABEL_LENGTH} characters`
    )
  });

  const formik = useFormik({
    initialValues: { label: "" },
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      resetForm();
    }
  });

  const {
    loading: caseLabelsLoading,
    error: caseLabelsError,
    caseLabels
  } = useGetCaseLabels();

  const { postCaseLabelCallback } = usePostCaseLabel();

  const updateSelectedLabels = (): void => {
    setSelectedLabels(
      caseLabels.filter(caseLabel =>
        labels.some(label => label.id === caseLabel.id)
      )
    );
  };

  // Initialise the selectedLabels on page load.
  useEffect(() => {
    if (!caseLabelsLoading || caseLabelsError) {
      updateSelectedLabels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseLabelsLoading, caseLabelsError]);

  const labelOptions = useMemo(() => {
    let labelOptions = [...caseLabels, ...newLabels];

    const trimmedNewLabel = newLabel.trim();
    const labelExists = labelOptions.some(
      label => label.name === trimmedNewLabel
    );

    // Only add the new label if it's not empty and doesn't already exist
    if (trimmedNewLabel && !labelExists) {
      labelOptions = [
        ...labelOptions,
        { id: -1, name: trimmedNewLabel, create_user: -1 }
      ];
    }

    return labelOptions;
  }, [caseLabels, newLabels, newLabel]);

  useEffect(() => {
    const prevLabels = prevLabelsRef.current;
    const labelsChanged =
      selectedLabels.length !== prevLabels.length ||
      selectedLabels.some((label, index) => label.id !== prevLabels[index]?.id);
    if (
      ((labelsUpdated && !caseLabelsLoading) || caseLabelsError) &&
      labelsChanged
    ) {
      const newModel = { ...caseModel, labels: selectedLabels };
      onChange({ isValid: true, model: newModel });
      prevLabelsRef.current = selectedLabels;
      setLabelsUpdated(false); // reset flag after update
    }
  }, [
    caseLabelsLoading,
    caseLabelsError,
    selectedLabels,
    caseModel,
    onChange,
    labelsUpdated
  ]);

  const addNewLabel = async (label: string): Promise<void> => {
    const postResponse = await postCaseLabelCallback({ name: label });
    // Add the new label to an array of "new labels"
    if (postResponse) {
      setNewLabels([...newLabels, postResponse]);
      setSelectedLabels([...selectedLabels, postResponse]); // update selectedLabels with the new label
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const trimmedNewLabel = replaceWhiteSpaces(newLabel).trim();

    if (event.key === "Enter") {
      event.preventDefault();
      if (
        trimmedNewLabel.length <= MAX_LABEL_LENGTH &&
        labelOptions.some(label => label.id === -1)
      ) {
        addNewLabel(trimmedNewLabel);
      }
    }

    // Prevent leading spaces before entering label name
    if (event.key === " " && !trimmedNewLabel) {
      event.preventDefault();
    }
  };

  const handleAutocompleteChange = (
    event: React.ChangeEvent<{}>,
    value: CaseLabel[]
  ): void => {
    if (value.length === 0) {
      setSelectedLabels([]);
      setLabelsUpdated(true);
      formik.setFieldValue("label", "");
      return;
    }

    // Create new label if the user selects a label that doesn't exist
    const newLabelSelected = value.find(label => label.id === -1);
    if (newLabelSelected) {
      addNewLabel(newLabelSelected.name);
    }

    setSelectedLabels(value);
    setLabelsUpdated(true);
  };

  return (
    <AddLabelWrapper>
      {Boolean(formik.errors.label) && (
        <ErrorMessage>{formik.errors.label}</ErrorMessage>
      )}
      <WidthConstrainedAutocomplete
        disabled={caseLabelsLoading}
        loading={caseLabelsLoading}
        label="Labels"
        placeholder=""
        value={selectedLabels}
        size="medium"
        multiple
        autoHighlight
        disableCloseOnSelect
        clearText="Clear All"
        autoComplete
        filterOptions={(
          options: CaseLabel[],
          { inputValue }: { inputValue: string }
        ) => {
          const labelName = inputValue.trim().toLowerCase();

          return options.filter(option =>
            option.name.toLowerCase().includes(labelName)
          );
        }}
        getOptionLabel={(option: CaseLabel) =>
          option.id === -1 ? `${option.name} (New Label)` : option.name
        }
        getOptionDisabled={(option: CaseLabel) =>
          option.name.length > MAX_LABEL_LENGTH
        }
        options={labelOptions}
        error={caseLabelsError || Boolean(formik.errors.label)}
        onKeyDown={handleKeyDown}
        inputValue={newLabel}
        onInputChange={(event: any, value: string) => {
          const sanitizedLabel = replaceWhiteSpaces(value);
          setNewLabel(sanitizedLabel);
          formik.setFieldValue("label", sanitizedLabel.trim());
        }}
        onChange={handleAutocompleteChange}
        renderTags={(
          value: CaseLabel[],
          getTagProps: AutocompleteRenderGetTagProps
        ) =>
          value.map((option, index) => {
            return (
              <Tooltip
                key={index}
                title={
                  option.name.length >= MAX_OPTION_LENGTH ? option.name : ""
                }
              >
                <SelectedLabelChip
                  label={option.name}
                  size="medium"
                  {...getTagProps({ index })}
                />
              </Tooltip>
            );
          })
        }
      />
    </AddLabelWrapper>
  );
};

const AddLabelWrapper = styled("div")`
  position: relative;
  margin-bottom: ${props => props.theme.spacing(2)};
`;
AddLabelWrapper.displayName = "AddLabelWrapper";

const WidthConstrainedAutocomplete = styled(Autocomplete)`
  padding-top: 8px;
`;
WidthConstrainedAutocomplete.displayName = "WidthConstrainedAutocomplete";

const ErrorMessage = styled("div")`
  color: #f44336;
  font-size: 0.75rem;
  position: absolute;
  top: 4px;
  right: 0;
`;
ErrorMessage.displayName = "ErrorMessage";

const SelectedLabelChip = styled(Chip)`
  height: 24px;
  max-width: ${MAX_OPTION_LENGTH}ch;

  & .MuiChip-deleteIcon {
    width: 16px;
    height: 16px;
  }
`;
SelectedLabelChip.displayName = "SelectedLabelChip";
